import * as React from 'react';
import { clsx } from 'clsx';
import * as Popover from '@radix-ui/react-popover';
import {
  ChevronDown,
  Undo2,
  Redo2,
  Search,
  Download,
  MessageSquare,
  Settings,
  Expand,
} from 'lucide-react';

export interface TableControlsProps {
  /**
   * Number of selected items for the Actions button
   */
  selectedCount?: number;
  /**
   * Callback when Actions dropdown is clicked
   */
  onActionsClick?: () => void;
  /**
   * Callback when Undo is clicked
   */
  onUndoClick?: () => void;
  /**
   * Callback when Redo is clicked
   */
  onRedoClick?: () => void;
  /**
   * Callback when Hide Columns is clicked
   */
  onHideColumnsClick?: () => void;
  /**
   * Callback when Search is clicked
   */
  onSearchClick?: () => void;
  /**
   * Callback when Download is clicked
   */
  onDownloadClick?: () => void;
  /**
   * Callback when Tooltips toggle is clicked
   */
  onTooltipsClick?: () => void;
  /**
   * Whether tooltips are enabled
   */
  tooltipsEnabled?: boolean;
  /**
   * Callback when Settings is clicked
   */
  onSettingsClick?: () => void;
  /**
   * Callback when Expand is clicked
   */
  onExpandClick?: () => void;
  /**
   * Whether undo is disabled
   */
  undoDisabled?: boolean;
  /**
   * Whether redo is disabled
   */
  redoDisabled?: boolean;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Custom style
   */
  style?: React.CSSProperties;
  /**
   * Test ID for testing
   */
  'data-testid'?: string;
}

const containerStyles: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingTop: '8px',
  paddingBottom: '8px',
  paddingLeft: 0,
  paddingRight: 0,
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const actions1Styles: React.CSSProperties = {
  display: 'flex',
  gap: '12px',
  alignItems: 'center',
};

const actions2Styles: React.CSSProperties = {
  display: 'flex',
  gap: '12px',
  alignItems: 'center',
  justifyContent: 'flex-end',
  flexGrow: 1,
  paddingRight: '4px',
};

const dropdownButtonStyles: React.CSSProperties = {
  height: '32px',
  backgroundColor: 'white',
  border: '1px solid #d1d1d1',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  paddingLeft: '12px',
  paddingRight: '8px',
  paddingTop: 0,
  paddingBottom: 0,
  cursor: 'pointer',
  transition: 'background-color 0.2s ease-in-out',
  outline: 'none',
};

const dropdownTextStyles: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 600,
  lineHeight: '1.5',
  color: '#2f2f2f',
  textAlign: 'center',
};

const iconButtonStyles: React.CSSProperties = {
  width: '32px',
  height: '32px',
  borderRadius: '99px',
  border: 'none',
  backgroundColor: 'white',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  transition: 'background-color 0.2s ease-in-out',
  flexShrink: 0,
  outline: 'none',
};

const iconButtonDisabledStyles: React.CSSProperties = {
  cursor: 'not-allowed',
  opacity: 0.5,
};

const popoverContentStyles: React.CSSProperties = {
  backgroundColor: 'white',
  border: '1px solid #d1d1d1',
  borderRadius: '8px',
  padding: '8px',
  minWidth: '180px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  zIndex: 1000,
};

const menuItemStyles: React.CSSProperties = {
  padding: '8px 12px',
  fontSize: '13px',
  color: '#2f2f2f',
  cursor: 'pointer',
  borderRadius: '4px',
  border: 'none',
  backgroundColor: 'white',
  width: '100%',
  textAlign: 'left',
  transition: 'background-color 0.2s ease-in-out',
};

/**
 * TableControls component - Arbor Design System
 *
 * A toolbar component with action buttons and controls for tables.
 */
export const TableControls = React.forwardRef<HTMLDivElement, TableControlsProps>(
  (
    {
      selectedCount = 0,
      onActionsClick,
      onUndoClick,
      onRedoClick,
      onHideColumnsClick,
      onSearchClick,
      onDownloadClick,
      onTooltipsClick,
      tooltipsEnabled = false,
      onSettingsClick,
      onExpandClick,
      undoDisabled = false,
      redoDisabled = false,
      className,
      style,
      'data-testid': dataTestId,
    },
    ref
  ) => {
    const [hoveredButton, setHoveredButton] = React.useState<string | null>(null);
    const [focusedButton, setFocusedButton] = React.useState<string | null>(null);
    const [actionsOpen, setActionsOpen] = React.useState(false);
    const [hideColumnsOpen, setHideColumnsOpen] = React.useState(false);

    const getIconButtonStyle = (
      buttonName: string,
      disabled: boolean = false
    ): React.CSSProperties => {
      if (disabled) {
        return { ...iconButtonStyles, ...iconButtonDisabledStyles };
      }

      const isHovered = hoveredButton === buttonName;
      const isFocused = focusedButton === buttonName;
      return {
        ...iconButtonStyles,
        backgroundColor: isHovered ? '#f5f5f5' : 'white',
        boxShadow: isFocused ? '0 0 0 3px #3cad51' : 'none',
      };
    };

    const getDropdownButtonStyle = (buttonName: string): React.CSSProperties => {
      const isHovered = hoveredButton === buttonName;
      const isFocused = focusedButton === buttonName;
      return {
        ...dropdownButtonStyles,
        backgroundColor: isHovered ? '#f5f5f5' : 'white',
        boxShadow: isFocused ? '0 0 0 3px #3cad51' : 'none',
      };
    };

    return (
      <div
        ref={ref}
        className={clsx('arbor-table-controls', className)}
        style={{ ...containerStyles, ...style }}
        data-testid={dataTestId}
      >
        {/* Left section - Actions 1 */}
        <div style={actions1Styles}>
          {onActionsClick && (
            <Popover.Root open={actionsOpen} onOpenChange={setActionsOpen}>
              <Popover.Trigger asChild>
                <button
                  type="button"
                  onClick={() => {
                    setActionsOpen(!actionsOpen);
                    onActionsClick();
                  }}
                  onMouseEnter={() => setHoveredButton('actions')}
                  onMouseLeave={() => setHoveredButton(null)}
                  onFocus={() => setFocusedButton('actions')}
                  onBlur={() => setFocusedButton(null)}
                  style={getDropdownButtonStyle('actions')}
                  aria-label="Actions"
                >
                  <span style={dropdownTextStyles}>Actions ({selectedCount})</span>
                  <ChevronDown size={16} color="#2f2f2f" />
                </button>
              </Popover.Trigger>
              <Popover.Portal>
                <Popover.Content
                  style={popoverContentStyles}
                  sideOffset={4}
                  align="start"
                >
                  <button
                    style={menuItemStyles}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
                    onClick={() => {
                      console.log('Edit selected items');
                      setActionsOpen(false);
                    }}
                  >
                    Edit selected items
                  </button>
                  <button
                    style={menuItemStyles}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
                    onClick={() => {
                      console.log('Delete selected items');
                      setActionsOpen(false);
                    }}
                  >
                    Delete selected items
                  </button>
                  <button
                    style={menuItemStyles}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
                    onClick={() => {
                      console.log('Export selected items');
                      setActionsOpen(false);
                    }}
                  >
                    Export selected items
                  </button>
                </Popover.Content>
              </Popover.Portal>
            </Popover.Root>
          )}

          {onUndoClick && (
            <button
              type="button"
              onClick={onUndoClick}
              disabled={undoDisabled}
              onMouseEnter={() => setHoveredButton('undo')}
              onMouseLeave={() => setHoveredButton(null)}
              onFocus={() => setFocusedButton('undo')}
              onBlur={() => setFocusedButton(null)}
              style={getIconButtonStyle('undo', undoDisabled)}
              aria-label="Undo"
            >
              <Undo2 size={16} color="#2f2f2f" />
            </button>
          )}

          {onRedoClick && (
            <button
              type="button"
              onClick={onRedoClick}
              disabled={redoDisabled}
              onMouseEnter={() => setHoveredButton('redo')}
              onMouseLeave={() => setHoveredButton(null)}
              onFocus={() => setFocusedButton('redo')}
              onBlur={() => setFocusedButton(null)}
              style={getIconButtonStyle('redo', redoDisabled)}
              aria-label="Redo"
            >
              <Redo2 size={16} color="#2f2f2f" />
            </button>
          )}

          {onHideColumnsClick && (
            <Popover.Root open={hideColumnsOpen} onOpenChange={setHideColumnsOpen}>
              <Popover.Trigger asChild>
                <button
                  type="button"
                  onClick={() => {
                    setHideColumnsOpen(!hideColumnsOpen);
                    onHideColumnsClick();
                  }}
                  onMouseEnter={() => setHoveredButton('hideColumns')}
                  onMouseLeave={() => setHoveredButton(null)}
                  onFocus={() => setFocusedButton('hideColumns')}
                  onBlur={() => setFocusedButton(null)}
                  style={getDropdownButtonStyle('hideColumns')}
                  aria-label="Hide Columns"
                >
                  <span style={dropdownTextStyles}>Hide Columns</span>
                  <ChevronDown size={16} color="#2f2f2f" />
                </button>
              </Popover.Trigger>
              <Popover.Portal>
                <Popover.Content
                  style={popoverContentStyles}
                  sideOffset={4}
                  align="start"
                >
                  <button
                    style={menuItemStyles}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
                    onClick={() => {
                      console.log('Hide Name column');
                      setHideColumnsOpen(false);
                    }}
                  >
                    Name
                  </button>
                  <button
                    style={menuItemStyles}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
                    onClick={() => {
                      console.log('Hide Email column');
                      setHideColumnsOpen(false);
                    }}
                  >
                    Email
                  </button>
                  <button
                    style={menuItemStyles}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
                    onClick={() => {
                      console.log('Hide Status column');
                      setHideColumnsOpen(false);
                    }}
                  >
                    Status
                  </button>
                  <button
                    style={menuItemStyles}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
                    onClick={() => {
                      console.log('Hide Date column');
                      setHideColumnsOpen(false);
                    }}
                  >
                    Date
                  </button>
                </Popover.Content>
              </Popover.Portal>
            </Popover.Root>
          )}
        </div>

        {/* Right section - Actions 2 */}
        <div style={actions2Styles}>
          {onSearchClick && (
            <button
              type="button"
              onClick={onSearchClick}
              onMouseEnter={() => setHoveredButton('search')}
              onMouseLeave={() => setHoveredButton(null)}
              onFocus={() => setFocusedButton('search')}
              onBlur={() => setFocusedButton(null)}
              style={getIconButtonStyle('search')}
              aria-label="Search"
            >
              <Search size={16} color="#2f2f2f" />
            </button>
          )}

          {onDownloadClick && (
            <button
              type="button"
              onClick={onDownloadClick}
              onMouseEnter={() => setHoveredButton('download')}
              onMouseLeave={() => setHoveredButton(null)}
              onFocus={() => setFocusedButton('download')}
              onBlur={() => setFocusedButton(null)}
              style={getIconButtonStyle('download')}
              aria-label="Download"
            >
              <Download size={16} color="#2f2f2f" />
            </button>
          )}

          {onTooltipsClick && (
            <button
              type="button"
              onClick={onTooltipsClick}
              onMouseEnter={() => setHoveredButton('tooltips')}
              onMouseLeave={() => setHoveredButton(null)}
              onFocus={() => setFocusedButton('tooltips')}
              onBlur={() => setFocusedButton(null)}
              style={getIconButtonStyle('tooltips')}
              aria-label={tooltipsEnabled ? 'Disable tooltips' : 'Enable tooltips'}
            >
              <MessageSquare
                size={16}
                color="#2f2f2f"
              />
            </button>
          )}

          {onSettingsClick && (
            <button
              type="button"
              onClick={onSettingsClick}
              onMouseEnter={() => setHoveredButton('settings')}
              onMouseLeave={() => setHoveredButton(null)}
              onFocus={() => setFocusedButton('settings')}
              onBlur={() => setFocusedButton(null)}
              style={getIconButtonStyle('settings')}
              aria-label="Settings"
            >
              <Settings size={16} color="#2f2f2f" />
            </button>
          )}

          {onExpandClick && (
            <button
              type="button"
              onClick={onExpandClick}
              onMouseEnter={() => setHoveredButton('expand')}
              onMouseLeave={() => setHoveredButton(null)}
              onFocus={() => setFocusedButton('expand')}
              onBlur={() => setFocusedButton(null)}
              style={getIconButtonStyle('expand')}
              aria-label="Expand"
            >
              <Expand size={16} color="#2f2f2f" />
            </button>
          )}
        </div>
      </div>
    );
  }
);

TableControls.displayName = 'TableControls';
